import React, { useState } from 'react';
import styles from './Home.module.css';
import { Link, useNavigate } from 'react-router-dom';
import GlobeSpinner from '../GlobeSpinner';
import { useUserData } from '../UserDataContext';
import defaultAvatar from '../../img/default-avatar.svg';
import Board from '../Board/Board';
import { Chess } from 'chess.js';

const Home = () => {
  const navigate = useNavigate();
  const {
    authLoading,
    currentUser,
    userData,
    friendsList,
    incomingRequests,
    games,
    gamesLoading,
    sendFriendRequest,
    acceptRequest,
    rejectRequest,
  } = useUserData();

  const [friendUsername, setFriendUsername] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [requestLoading, setRequestLoading] = useState(false);

  const handleSendRequest = async () => {
    if (!friendUsername.trim()) {
      setError('Please enter a username.');
      return;
    }
    setRequestLoading(true);
    setError('');
    setSuccess('');
    try {
      const result = await sendFriendRequest(friendUsername.trim());
      setFriendUsername('');
      if (result === 'accepted') {
        setSuccess(`You and ${friendUsername.trim()} are now friends!`);
      } else {
        setSuccess('Friend request sent!');
      }
      setTimeout(() => setSuccess(''), 4000);
    } catch (e) {
      setError(e.message || 'An error occurred.');
    } finally {
      setRequestLoading(false);
    }
  };

  const handleAccept = async (req) => {
    try {
      await acceptRequest(req.id, req.fromUsername);
    } catch (e) {
      setError(e.message || 'Failed to accept request.');
    }
  };

  const handleReject = async (req) => {
    try {
      await rejectRequest(req.id);
    } catch (e) {
      setError(e.message || 'Failed to reject request.');
    }
  };

  const handlePlay = (friendUsername) => {
    navigate("/play", { state: { friendUsername } });
  };

  const handleA = (gameId) => {
    navigate(`/analysis/${gameId}`);
  };

  if (authLoading) {
    return (
      <div>
        <div className={styles.container}>
          <div className={styles.content}>
            <GlobeSpinner size={80} />
          </div>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div>
        <div className={styles.container}>
          <div className={styles.hero}>
            <h1>Chess64</h1>
            <p className={styles.heroSub}>Play, analyze, and improve your chess game online.</p>
            <div className={styles.heroCta}>
              <Link to="/login">
                <button className={styles.custombutton}>Log In</button>
              </Link>
              <Link to="/signup">
                <button className={`${styles.custombutton} ${styles.custombuttonAlt}`}>Create Account</button>
              </Link>
            </div>
            <div className={styles.heroFeatures}>
              <div className={styles.featureCard}>
                <span className={styles.featureIcon}>&#9814;</span>
                <h3>Play Online</h3>
                <p>Challenge friends to timed matches</p>
              </div>
              <div className={styles.featureCard}>
                <span className={styles.featureIcon}>&#9881;</span>
                <h3>Engine Analysis</h3>
                <p>Stockfish-powered move evaluation</p>
              </div>
              <div className={styles.featureCard}>
                <span className={styles.featureIcon}>&#9816;</span>
                <h3>Play Locally</h3>
                <p>Practice against the computer</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {userData && userData.map((user, index) => (
          <div key={index}>
            <h1>Welcome back, {user.username || 'Player'}!</h1>
          </div>
        ))}
        {!userData && <h1>Loading profile...</h1>}
        <div className={styles.playCtaRow}>
          <button
            className={`${styles.custombutton} ${styles.custombuttonAlt}`}
            style={{ padding: '16px 36px', fontSize: '16px' }}
            onClick={() => navigate('/play')}
          >
            &#9823; Play Now
          </button>
          <button
            className={styles.custombutton}
            onClick={() => navigate('/puzzles')}
          >
            &#9816; Puzzles
          </button>
        </div>
      </div>
      <div className={styles.user}>
        {(() => {
  const activeGames = games.filter(g => {
    if (g.status !== 'ready' || g.result !== 'unknown') return false;
    if (g.fen) {
      try { if (new Chess(g.fen).isGameOver()) return false; } catch (e) {}
    }
    return true;
  });
  return (
    <div className={styles.currentGamesSection}>
      <h3>Current Games</h3>
      {activeGames.length === 0 ? (
        <p className={styles.emptyText}>Ongoing games will appear here when available.</p>
      ) : (
        <div className={styles.currentGameCards}>
          {activeGames.map((game, index) => (
            <div key={index} className={styles.currentGameCard}>
              <div className={styles.miniBoard}>
                <Board
                  chess={null}
                  currFen={game.fen || 'start'}
                  side={game.memberPiece === 'b' ? 'black' : 'white'}
                  draggable={false}
                  notation={false}
                  calcWidth={() => 170}
                />
              </div>
              <span className={styles.currentGameOpponent}>vs {game.opponentName}</span>
              <button className={styles.custombutton} onClick={() => navigate(`/game/${game.gameId}`)}>
                Rejoin
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
})()}
        <div className={styles.dashboardGrid}>
          <div className={styles.dashboardCard}>
            <h3>Game History</h3>
            <div className={styles.cardScroll}>
              {gamesLoading ? (
                <GlobeSpinner size={50} />
              ) : games.filter(g => g.result !== 'unknown').length > 0 ? games.filter(g => g.result !== 'unknown').map((game, index) => (
                <div key={index} className={styles.cardRow}>
                  <div className={styles.gameInfo}>
                    <img
                      src={game.opponentPic || defaultAvatar}
                      alt=""
                      className={styles.opponentPic}
                    />
                    <span className={styles.opponentName}>{game.opponentName}</span>
                    <span className={
                      game.result === 'win' ? styles.resultWin :
                      game.result === 'loss' ? styles.resultLoss :
                      styles.resultDraw
                    }>
                      {game.result === 'win' ? 'W' : game.result === 'loss' ? 'L' : game.result === 'draw' ? 'D' : '—'}
                    </span>
                  </div>
                  <button className={styles.custombutton} onClick={() => handleA(game.gameId)}>Analyze</button>
                </div>
              )) : (
                <div style={{ textAlign: 'center' }}>
                  <p className={styles.emptyText}>No games yet.</p>
                  <button className={styles.custombutton} onClick={() => navigate('/play')}>
                    Find an opponent
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className={styles.dashboardCard}>
            <h3>Friends</h3>
            <div className={styles.addFriendRow}>
              <input
                type="text"
                placeholder="Enter username"
                value={friendUsername}
                onChange={(e) => setFriendUsername(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSendRequest()}
                disabled={requestLoading}
              />
              <button
                className={styles.custombutton}
                onClick={handleSendRequest}
                disabled={requestLoading}
              >
                {requestLoading ? '...' : 'Add'}
              </button>
            </div>
            {error && <p className={styles.error}>{error}</p>}
            {success && <p className={styles.successMsg}>{success}</p>}

            {incomingRequests.length > 0 && (
              <div className={styles.requestsSection}>
                <h4 className={styles.requestsTitle}>Friend Requests</h4>
                {incomingRequests.map((req) => (
                  <div key={req.id} className={styles.requestCard}>
                    <div className={styles.requestInfo}>
                      <img
                        src={req.fromPic || defaultAvatar}
                        alt=""
                        className={styles.requestPic}
                      />
                      <span className={styles.requestName}>{req.fromUsername}</span>
                    </div>
                    <div className={styles.requestActions}>
                      <button className={styles.acceptBtn} onClick={() => handleAccept(req)}>&#10003;</button>
                      <button className={styles.rejectBtn} onClick={() => handleReject(req)}>&#10005;</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className={styles.cardScroll}>
              {friendsList.length > 0 ? friendsList.map((friend, index) => (
                <div key={index} className={styles.cardRow}>
                  <div className={styles.gameInfo}>
                    <img
                      src={friend.profilePicture || defaultAvatar}
                      alt=""
                      className={styles.opponentPic}
                    />
                    <span className={styles.opponentName}>{friend.username}</span>
                  </div>
                  <button className={styles.custombutton} onClick={() => handlePlay(friend.username)}>Play</button>
                </div>
              )) : <p className={styles.emptyText}>No friends added yet.</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
